// Voice-message waveform peaks — a progressive enhancement over the plain seek
// bar, never a dependency. On any error/timeout getPeaks resolves null and the
// player silently keeps the A1 bar. Playback never waits on this.
//
// Pipeline: in-session cache → fetch(src) → arrayBuffer → decodeAudioData →
// hand channel-0 to the shared worker for max-abs bucketing → Float32Array.
//
// Decode stays on the main thread on purpose: decodeAudioData is async and
// runs the actual decode off-thread internally, but it only exists on
// AudioContext/OfflineAudioContext, which a plain (non-AudioWorklet) Worker
// can't construct — so a worker can't decode. Only the cheap bucketing loop is
// offloaded.

import { getLogger } from '@deltachat-desktop/shared/logger'

const log = getLogger('AudioPlayer/peaks')

const N_PEAKS = 64
const TIME_BUDGET_MS = 4000
const CACHE_MAX = 100

// ponytail: bounded in-session cache, NO persistent peak cache (issue A2.5 —
// measure first). FIFO by insertion order, ~100 entries keyed by src. The
// in-flight PROMISE is cached (not the settled value) so two bubbles mounting
// the same src share one pipeline; a failed src stays cached as a null-promise
// and isn't retried every render.
const cache = new Map<string, Promise<Float32Array | null>>()

// --- User Timing (sc:waveform:*), all best-effort ------------------------
function measure(
  name: string,
  start: number,
  end: number,
  detail: { durationSec: number; bytes: number }
): void {
  try {
    performance.measure(name, { start, end, detail })
  } catch {
    // performance.measure can throw in exotic environments; timing is best-effort
  }
}
function fallbackMark(reason: string): void {
  try {
    performance.mark('sc:waveform:fallback', { detail: { reason } })
  } catch {
    /* best-effort */
  }
}

// --- shared bucketing worker (lazy, module-level) ------------------------
// undefined = not yet tried, null = construction failed (fallback forever).
let worker: Worker | null | undefined
let nextId = 0
const pending = new Map<number, (peaks: Float32Array | null) => void>()

function getWorker(): Worker | null {
  if (worker !== undefined) {
    return worker
  }
  try {
    // Resolve relative to the document (like the web-app's BASE convention for
    // core/worker.js): a root-absolute '/waveform-worker.js' 404s on subpath
    // deploys (e.g. GitHub Pages project sites).
    worker = new Worker(new URL('waveform-worker.js', location.href), {
      type: 'module',
    })
    worker.onmessage = (e: MessageEvent) => {
      const { id, peaks } = e.data
      const resolve = pending.get(id)
      if (resolve) {
        pending.delete(id)
        resolve(peaks)
      }
    }
    // A load error (targets that don't serve the file) fires asynchronously and
    // does NOT throw above. Treat any worker error as a permanent fallback: drop
    // the worker so getWorker() never hands out a dead one again, and settle
    // in-flight callers now (releasing their retained AudioBuffers) instead of
    // leaking one `pending` entry each until the 4s budget.
    worker.onerror = () => {
      log.debug('waveform worker failed; permanent fallback')
      worker?.terminate()
      worker = null
      for (const resolve of pending.values()) {
        resolve(null)
      }
      pending.clear()
    }
  } catch {
    // Some environments don't serve the file / block module workers.
    worker = null
  }
  return worker
}

function bucketInWorker(
  channel: Float32Array,
  n: number
): Promise<Float32Array | null> {
  const w = getWorker()
  if (w == null) {
    return Promise.resolve(null)
  }
  return new Promise(resolve => {
    const id = nextId++
    pending.set(id, resolve)
    w.postMessage({ id, data: channel, n }, [channel.buffer])
  })
}

// --- pipeline ------------------------------------------------------------
async function computePeaks(
  src: string,
  signal: AbortSignal
): Promise<Float32Array | null> {
  // Worker known dead (load failed / unavailable): skip the pointless fetch +
  // main-thread decode and fall back to the plain bar. Also warms the worker on
  // the first call, in parallel with the fetch below.
  if (getWorker() == null) {
    fallbackMark('no-worker')
    return null
  }
  try {
    const t0 = performance.now()
    const resp = await fetch(src, { signal })
    const bytes = await resp.arrayBuffer()
    // Read now: decodeAudioData detaches the buffer, after which byteLength
    // reports 0.
    const byteLength = bytes.byteLength
    const t1 = performance.now()

    const Ctx =
      (window as any).OfflineAudioContext ||
      (window as any).webkitOfflineAudioContext
    // Length/sampleRate are irrelevant to a decode-only context.
    const ctx: OfflineAudioContext = new Ctx(1, 1, 44100)
    const audioBuffer = await ctx.decodeAudioData(bytes)
    const t2 = performance.now()

    // slice() so we transfer a copy and don't neuter the AudioBuffer's channel.
    const channel = audioBuffer.getChannelData(0).slice()
    const peaks = await bucketInWorker(channel, N_PEAKS)
    const t3 = performance.now()

    // The 4s budget already fired (result discarded, 'timeout' marked): don't
    // pile success measures onto a run whose peaks were thrown away.
    if (signal.aborted) {
      return null
    }
    // Worker died mid-flight (onerror flushed `pending` with null): fall back
    // instead of reporting a phantom success.
    if (peaks == null) {
      fallbackMark('no-worker')
      return null
    }

    const detail = {
      durationSec: audioBuffer.duration,
      bytes: byteLength,
    }
    measure('sc:waveform:fetch', t0, t1, detail)
    measure('sc:waveform:decode', t1, t2, detail)
    measure('sc:waveform:bucket', t2, t3, detail)
    return peaks
  } catch (e) {
    // Aborted by the budget timeout is already marked as 'timeout'.
    if (!signal.aborted) {
      log.debug('waveform peaks failed', e)
      fallbackMark('error')
    }
    return null
  }
}

/**
 * Peaks for the given (already transformed) audio URL, or null to fall back to
 * the plain seek bar. Bounded ~4s budget; the in-flight promise is cached for
 * the session so concurrent callers for the same src share one pipeline.
 */
export function getPeaks(src: string): Promise<Float32Array | null> {
  // Srcs carry a per-message #msg fragment (forward vs original of the same
  // deduped blob must not share mirroring/positions) — but their AUDIO is
  // identical, so peaks are cached and fetched by the fragment-less URL: one
  // decode per blob, not per message.
  const blobUrl = src.split('#')[0]
  const hit = cache.get(blobUrl)
  if (hit !== undefined) {
    return hit
  }
  const ac = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<null>(resolve => {
    timer = setTimeout(() => {
      ac.abort()
      fallbackMark('timeout')
      resolve(null)
    }, TIME_BUDGET_MS)
  })
  const result = Promise.race([computePeaks(blobUrl, ac.signal), timeout])
    .catch(() => null)
    .finally(() => clearTimeout(timer))

  cache.set(blobUrl, result)
  if (cache.size > CACHE_MAX) {
    const oldest = cache.keys().next().value
    if (oldest !== undefined) {
      cache.delete(oldest)
    }
  }
  return result
}
